import { getNewFileStarter, type NewFileStarter } from '../fileBrowser/fileTypeRegistry';
import type { MyAppsWorkspaceStarterId } from '../fileBrowser/createBlankStarterInUserStorage';

export type CodeEditorTemplate = {
  starterId: MyAppsWorkspaceStarterId;
  title: string;
  description: string;
  extension: string;
};

/** Same language/config set offered by My Apps → Code Editor and the Code Editor empty state. */
export const CODE_EDITOR_TEMPLATES: readonly CodeEditorTemplate[] = [
  { starterId: 'json', title: 'JSON', description: 'Structured data', extension: '.json' },
  { starterId: 'jsonc', title: 'JSONC', description: 'JSON with comments', extension: '.jsonc' },
  { starterId: 'xml', title: 'XML', description: 'Markup document', extension: '.xml' },
  { starterId: 'yaml', title: 'YAML', description: 'Config / data', extension: '.yaml' },
  { starterId: 'js', title: 'JavaScript', description: 'JS source', extension: '.js' },
  { starterId: 'ts', title: 'TypeScript', description: 'TS source', extension: '.ts' },
  { starterId: 'tsx', title: 'TSX', description: 'React / TSX', extension: '.tsx' },
  { starterId: 'py', title: 'Python', description: 'Python script', extension: '.py' },
  { starterId: 'ps1', title: 'PowerShell', description: 'PS1 script', extension: '.ps1' },
  { starterId: 'cs', title: 'C#', description: 'C# source', extension: '.cs' },
  { starterId: 'sql', title: 'SQL', description: 'SQL script', extension: '.sql' },
  { starterId: 'sh', title: 'Shell', description: 'Bash / shell', extension: '.sh' },
  { starterId: 'html', title: 'HTML', description: 'Web page', extension: '.html' },
  { starterId: 'css', title: 'CSS', description: 'Stylesheet', extension: '.css' },
  { starterId: 'ini', title: 'Config', description: 'INI / plain config', extension: '.ini' }
];

export function defaultCodeEditorFileName(starterId: MyAppsWorkspaceStarterId): string {
  return getNewFileStarter(starterId)?.defaultName ?? `file.${starterId}`;
}

export function validateCodeEditorFileName(fileName: string, extension: string): string | null {
  const trimmed = fileName.trim();
  if (!trimmed) return 'File name is required.';
  if (trimmed.includes('/') || trimmed.includes('\\')) {
    return 'File name cannot contain path separators.';
  }
  if (!trimmed.toLowerCase().endsWith(extension.toLowerCase())) {
    return `File name must end with ${extension}.`;
  }
  return null;
}

export function getCodeEditorTemplateStarter(starterId: string): NewFileStarter | undefined {
  const starter = getNewFileStarter(starterId);
  if (!starter || starter.stub || starter.openSurface !== 'code') return undefined;
  return starter;
}
